import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useGetAiEligibilityQuery } from "src/_api/aiSupportApi";
import { ROLES, USER_ROLES } from "src/constants";

/** The merchant and admin dashboards: `/merchant`, `/admin` and below. */
const CHAT_PATH = /^\/(merchant|admin)(\/|$)/;

/**
 * Client pre-gate (plan §3.1). It only avoids pointless `/eligibility`
 * requests; the backend decides. Admins pass (the backend allows only AI
 * developers), and so do BASIC merchant users, owners and sub-admins.
 * Shipping companies also live under `/merchant`, so the role check is what
 * keeps them out (C14). Guards, drivers and public pages never pass.
 *
 * @param {object | null | undefined} profile `state.entities.profile.profile`
 * @param {string} [pathname]
 * @returns {boolean}
 */
export const isPreGateOpen = (profile, pathname) =>
  isRoleGateOpen(profile) && CHAT_PATH.test(pathname || "");

/**
 * The role half of the pre-gate: admins and BASIC merchant users.
 *
 * @param {object | null | undefined} profile
 * @returns {boolean}
 */
export const isRoleGateOpen = (profile) => {
  if (!profile) return false;
  const isAdmin = profile.role === USER_ROLES.ADMIN;
  const isBasicMerchant =
    profile.role === USER_ROLES.MERCHANT &&
    profile.merchant?.role === ROLES.merchant;
  return isAdmin || isBasicMerchant;
};

/**
 * Who sees the chat: the pre-gate, then `GET /ai-support/eligibility`.
 *
 * The query arg is the profile id: the endpoint ignores it, but it gives
 * every user their own cache entry, so a login that skipped `logout()` (and
 * its cache reset) never reuses the previous user's answer (D31). Every
 * caller must go through this hook to share that entry.
 *
 * - `visible`: show the launcher and the panel. Read from `currentData`
 *   (this user's entry only), which RTK Query keeps when a background
 *   refetch fails, so a network blip never unmounts the chat mid-stream
 *   (C15).
 * - `mounted`: keep the chat itself (provider, live answer) alive. Also true
 *   outside `/merchant` and `/admin` while this user's last answer said
 *   enabled: leaving the dashboards for a moment (the app bar logo goes to
 *   `/`, unknown paths go to `/errors/404`) must not abort a streaming
 *   answer or drop the chat state. No request is sent there.
 * - `isAiDeveloper`: independent of `enabled`; the kill switch turns the chat
 *   off but an AI developer still needs the admin page to turn it back on.
 * - `eligibility`: the raw query result (`data`, `refetch`, …).
 *
 * @returns {{ visible: boolean, mounted: boolean, isAiDeveloper: boolean,
 *   preGate: boolean, eligibility: object }}
 */
export default function useAiEligibility() {
  const profile = useSelector((state) => state.entities.profile.profile);
  const { pathname } = useLocation();
  const preGate = isPreGateOpen(profile, pathname);
  const profileId = profile?.id;

  const eligibility = useGetAiEligibilityQuery(profileId, {
    skip: !preGate,
  });
  const data = preGate ? eligibility.currentData : undefined;

  // This user's last answer, for `mounted` outside the dashboards
  const [kept, setKept] = useState(null);
  useEffect(() => {
    if (data && profileId) {
      setKept({ profileId, enabled: data.enabled === true });
    }
  }, [data, profileId]);
  const keptEnabled =
    isRoleGateOpen(profile) &&
    kept?.profileId === profileId &&
    kept.enabled === true;

  return {
    visible: data?.enabled === true,
    mounted: data ? data.enabled === true : keptEnabled,
    isAiDeveloper: data?.isAiDeveloper === true,
    preGate,
    eligibility,
  };
}
